import isEmpty from 'lodash/isEmpty';
import isNil from 'lodash/isNil';
import omit from 'lodash/omit';
import pick from 'lodash/pick';
import type { UseFormClearErrors, UseFormSetValue } from 'react-hook-form';
import type { IntlShape } from 'react-intl';

import { CountryCodeEnum } from '@/types/regionTypes';

import type { AddressSchema } from '@/components/AddressForm';
import { getTitleOptionByValue } from '@/components/FormFields/TitleField';
import type { SelectOption } from '@/components/Select';
import { initializeApollo } from '@/graphql/client';
import type {
  AccountAddressCreateMutation,
  AccountAddressCreateMutationVariables,
  AccountAddressUpdateMutation,
  AccountAddressUpdateMutationVariables,
  AccountSetDefaultAddressMutation,
  AccountSetDefaultAddressMutationVariables,
  AddressDeleteMutation,
  AddressDeleteMutationVariables,
  AddressFragment,
  AddressInput,
  AddressQuery,
  AddressQueryVariables,
  ChoiceValue,
  CountryCode,
  MetadataInput,
  MetadataItem,
} from '@/graphql/saleor/api';
import {
  AccountAddressCreateDocument,
  AccountAddressUpdateDocument,
  AccountSetDefaultAddressDocument,
  AddressDeleteDocument,
  AddressDocument,
} from '@/graphql/saleor/api';
import type { AddressData } from '@/hooks/useAddressSearch';
import type { Maybe } from '@/lib/types';
import type { ValidationRules } from '@/providers/ConfigProvider';
import type { ServiceAddressSchema } from '@/views/ServiceDetailsView/components/ServiceAddressSection/schema';

import { formatAddressForCountry } from './checkout';
import { generateFieldPrefix } from './forms/helpers';
import { ACCOUNT_METADATA } from './metadata/const';
import { getPhoneCodeAndNumber } from './phone';
import type { ServiceAddress } from './service/type';

// We need this because Salero doesn't respect the same standard as Melissa
export const COUNTRYWITHREVERSECODE = ['Italy'];

export const getValidationChoiceValue = (
  choices: Maybe<ChoiceValue[]>,
  choice: Maybe<ChoiceValue>,
  value: Maybe<string>,
): Maybe<string> | SelectOption => {
  if (choices?.length) {
    if (choice) {
      return {
        label: choice.verbose!,
        value: choice.raw!,
      };
    }
    return '';
  }
  return value;
};

export const addressToSchema = (
  titleValue: Maybe<string>,
  address: Maybe<AddressFragment>,
  countryAreaChoices: ValidationRules['countryAreaChoices'],
  cityChoices: ValidationRules['cityChoices'],
  formatMessage: IntlShape['formatMessage'],
  firstName?: Maybe<string>,
  lastName?: Maybe<string>,
): AddressSchema => {
  const countryAreaChoice = countryAreaChoices.find(
    (choice) => choice.raw === address?.countryArea,
  );
  const cityChoice = cityChoices.find((choice) => choice.raw === address?.city);
  const resolvedCity = getValidationChoiceValue(
    cityChoices,
    cityChoice,
    address?.city,
  );
  // A Melissa locality is a raw string. When the market has a city dropdown and
  // that string is not an exact choice, keep a display value the select can
  // show. Do not add it to the choice list.
  const city =
    resolvedCity || !cityChoices.length || !address?.city
      ? (resolvedCity ?? '')
      : {
          label: address.city,
          value: address.city,
        };
  const isJapaneseMarket = address?.country.code === CountryCodeEnum.JP;

  const countryCode = address?.country.code as CountryCode;
  const titleOption = getTitleOptionByValue(
    formatMessage,
    countryCode,
    titleValue ??
      (!address?.metadata
        ? undefined
        : getMetaValue(ACCOUNT_METADATA.personalTitle, address?.metadata)),
  );

  return {
    country:
      address?.country &&
      ({
        label: address.country.country,
        value: address.country.code as CountryCode,
      } as SelectOption<CountryCode>),
    city,
    companyName: address?.companyName ?? '',
    countryArea: getValidationChoiceValue(
      countryAreaChoices,
      countryAreaChoice,
      address?.countryArea,
    ),
    firstName: address?.firstName ?? firstName ?? '',
    lastName: address?.lastName ?? lastName ?? '',
    phone: address?.phone ?? '',
    streetName: address?.streetAddress1 ?? '',
    streetNumber: '',
    title: titleOption,
    streetAddress2: address?.streetAddress2 || '',
    postalCode: address?.postalCode ?? '',
    apartmentNumber: isJapaneseMarket
      ? getMetaValue(ACCOUNT_METADATA.apartmentNumber, address?.metadata ?? [])
      : '',
    houseMansionName: isJapaneseMarket
      ? getMetaValue(ACCOUNT_METADATA.houseMansionName, address?.metadata ?? [])
      : '',
    vatNumber: getMetaValue(
      ACCOUNT_METADATA.vatNumber,
      address?.metadata ?? [],
    ),
    abn: getMetaValue(ACCOUNT_METADATA.vatNumber, address?.metadata ?? []),
  };
};

// @TODO : Duplicatate with getMetaValue in src/lib/metadata/helpers.ts
export const getMetaValue = (key: string, metadata: MetadataItem[]): string => {
  if (isEmpty(metadata) || isEmpty(key)) return '';
  const _res = metadata.find((item) => item.key === key);
  return _res?.value || '';
};

const getMixedValue = <T extends string>(value: Maybe<SelectOption<T> | T>) => {
  if (typeof value === 'object') {
    return value?.value ?? null;
  }
  return value ?? null;
};
export const schemaToInput = (address: AddressSchema): AddressInput => {
  const pickKeys: Array<keyof AddressInput> = [
    'firstName',
    'lastName',
    'companyName',
    'streetAddress2',
    'streetAddress1',
    'cityArea',
    'postalCode',
    'phone',
  ];

  const isJapan = address.country?.value === CountryCodeEnum.JP;
  const isUK = address.country?.value === CountryCodeEnum.GB;
  const isAustralia = address.country?.value === CountryCodeEnum.AU;

  const japanMetadata = isJapan
    ? [
        {
          key: ACCOUNT_METADATA.first_name_kanji,
          value: address.firstName ?? '',
        },
        {
          key: ACCOUNT_METADATA.last_name_kanji,
          value: address.lastName ?? '',
        },
        ...(address.houseMansionName
          ? [
              {
                key: ACCOUNT_METADATA.houseMansionName,
                value: address.houseMansionName,
              },
            ]
          : []),
        ...(address.apartmentNumber
          ? [
              {
                key: ACCOUNT_METADATA.apartmentNumber,
                value: address.apartmentNumber,
              },
            ]
          : []),
      ]
    : [];

  const taxMetadata = [
    ...(isUK && address.vatNumber
      ? [{ key: ACCOUNT_METADATA.vatNumber, value: address.vatNumber }]
      : []),
    ...(isAustralia && address.abn
      ? [{ key: ACCOUNT_METADATA.vatNumber, value: address.abn }]
      : []),
  ];

  const titleMetadata = address.title?.value
    ? [{ key: ACCOUNT_METADATA.personalTitle, value: address.title.value }]
    : [];

  const allMetadata = [...titleMetadata, ...japanMetadata, ...taxMetadata];

  return {
    ...pick(address, pickKeys),
    ...(allMetadata.length > 0 && { metadata: allMetadata }),
    streetAddress1: address.streetName,
    country: getMixedValue(address?.country),
    city: getMixedValue(address?.city),
    countryArea: getMixedValue(address?.countryArea),
  };
};

export const addressToInput = ({
  country,
  metadata,
  ...address
}: AddressFragment): AddressInput => {
  const omitKeys: Array<keyof AddressFragment> = [
    'id',
    '__typename',
    'isDefaultBillingAddress',
    'isDefaultShippingAddress',
  ];

  const addressInput = omit(address, omitKeys);
  return {
    ...addressInput,
    country: country.code as CountryCode,
    metadata: Array.isArray(metadata)
      ? metadata.map((item) => omit(item, '__typename'))
      : metadata,
  };
};

export const schemaToAddressFragment = (
  address: AddressSchema,
  phone?: string,
): AddressFragment => {
  const pickKeys: Array<keyof AddressInput> = [
    'firstName',
    'lastName',
    'companyName',
    'streetAddress2',
    'streetAddress1',
    'cityArea',
    'postalCode',
    'phone',
  ];
  const obj = {
    __typename: 'Address',
    id: '',
    cityArea: '',
    metadata: isNil(address.title?.value)
      ? []
      : [
          {
            key: ACCOUNT_METADATA.personalTitle,
            value: address.title?.value,
          },
        ],
    ...pick(address, pickKeys),
    streetAddress1: address.streetName ?? '',
    country: {
      code: getMixedValue(address?.country) ?? '',
      country: address.country?.label ?? '',
    },
    city: getMixedValue(address?.city),
    countryArea: getMixedValue(address?.countryArea),
  } as AddressFragment;
  if (phone) {
    obj.phone = phone;
  }
  return obj;
};

const ecsState = (country: string | null | undefined, state: string) =>
  country === 'DE' ? '' : state;

export const omitGermanCountryArea = <
  T extends {
    country?: { code?: string | null } | null;
    countryArea?: string | null;
  },
>(
  address: T,
): T =>
  address.country?.code === 'DE' ? { ...address, countryArea: '' } : address;

export const AddressFragmentToServiceAddress = (
  address: AddressFragment,
  email?: string,
): ServiceAddress => {
  const { phoneCode, phoneNumber } = getPhoneCodeAndNumber(address.phone);
  const phone = phoneNumber ? phoneCode + phoneNumber : '';
  const titleMeta = getMetaValue(
    ACCOUNT_METADATA.personalTitle,
    address.metadata,
  );
  const title = ['Mr', 'Mrs', 'Ms'].includes(titleMeta) ? titleMeta : null;

  const addr: ServiceAddress = {
    addressType: 'Main',
    title,
    address1: address.companyName,
    address2: address.streetAddress1,
    address3: address.streetAddress2,
    city: address.city,
    state: ecsState(address.country.code, address.countryArea),
    country: address.country.code,
    email: email ?? '',
    firstName: address.firstName,
    lastName: address.lastName,
    mobileNumber: phone,
    phoneNumber: phone,
    zipCode: address.postalCode,
  };
  return addr;
};

export const serviceAddressToAddressFragment = (
  address: ServiceAddress,
): AddressFragment => {
  const pickKeys: Array<keyof AddressInput> = [
    'firstName',
    'lastName',
    'companyName',
    'streetAddress2',
    'streetAddress1',
    'cityArea',
    'postalCode',
    'phone',
  ];
  const obj = {
    __typename: 'Address',
    id: '',
    cityArea: '',
    metadata: [
      {
        key: ACCOUNT_METADATA.personalTitle,
        value: address.title,
      },
    ],
    ...pick(address, pickKeys),
    firstName: address.firstName ?? '',
    lastName: address.lastName ?? '',
    companyName: address.address1 ?? '',
    streetAddress1: address.address2 ?? '',
    streetAddress2: address.address3 ?? '',
    postalCode: address.zipCode ?? '',
    phone: address.phoneNumber ?? address.mobileNumber ?? '',
    country: {
      code: getMixedValue(address.country) ?? '',
      country: '',
    },
    city: getMixedValue(address?.city),
    countryArea: getMixedValue(address?.state),
  } as AddressFragment;
  return obj;
};

export const serviceAddressSchemaToServiceAddress = (
  address: ServiceAddressSchema,
  email?: string,
): ServiceAddress => {
  const addr: ServiceAddress = {
    addressType: 'Main',
    title: address.title?.value ?? '',
    address1: address.companyName ?? '',
    address2: address.streetName ?? '',
    address3: address.streetAddress2 ?? '',
    city: address.city,
    state: ecsState(address.country?.value, address.countryArea?.value ?? ''),
    country: address.country?.value ?? '',
    email: email ?? '',
    firstName: address.firstName,
    lastName: address.lastName,
    mobileNumber: address.phone!,
    phoneNumber: address.phone!,
    zipCode: address.postalCode ?? '',
  };
  return addr;
};

export const isSameAddress = <T extends AddressFragment | AddressInput>(
  addressA: Maybe<T>,
  addressB: Maybe<T>,
  skipFields: Array<keyof T> = [],
  compareFields: Array<keyof T> = [
    'streetAddress1',
    'streetAddress2',
    'city',
    'country',
    'postalCode',
    'countryArea',
    'cityArea',
    'firstName',
    'lastName',
    'phone',
  ],
) => {
  if (!addressA || !addressB) {
    return false;
  }

  return Object.entries(addressA).every(([name, value]) => {
    if (
      !compareFields?.includes(name as keyof T) ||
      skipFields?.includes(name as keyof T)
    ) {
      return true;
    }

    if (name === 'country') {
      if ('__typename' in addressA) {
        // Only addressA is known to be a full fragment - addressB may be a
        // partially populated address, so its country cannot be dereferenced
        // blindly. A missing country on either side means "not the same",
        // never "both blank, therefore equal".
        const codeA = (value as AddressFragment['country'] | null)?.code;
        const codeB = (
          addressB?.[name] as AddressFragment['country'] | null | undefined
        )?.code;

        return codeA != null && codeB != null && codeA === codeB;
      } else {
        return value === (addressB as AddressInput).country;
      }
    }

    // Both values are null
    if (value == null && addressB[name as keyof T] == null) {
      return true;
    }

    // One value is null and the other is not
    if (
      (value == null && addressB[name as keyof T] != null) ||
      (value != null && addressB[name as keyof T] == null)
    ) {
      return false;
    }

    // Both values are non-null, so we trim them and then compare them and return the result
    return (
      (value as string).trim() === (addressB[name as keyof T] as string).trim()
    );
  });
};

export const setAddressFromSuggestion = (
  prefix: string,
  address: AddressData,
  validationRules: Maybe<ValidationRules>,
  setter: UseFormSetValue<any>,
  clearErrors: UseFormClearErrors<any>,
) => {
  const withSchemaPrefix = generateFieldPrefix(prefix);
  setter(`${withSchemaPrefix('postalCode')}`, address.PostalCode);
  setter(`${withSchemaPrefix('city')}`, address.Locality);
  setter(`${withSchemaPrefix('streetName')}`, address.DeliveryAddress);
  // A suggestion carries no apartment line, so anything left from a previously
  // selected address has to go — otherwise the old flat/unit is silently
  // attached to the new street.
  setter(`${withSchemaPrefix('streetAddress2')}`, '');

  if (validationRules) {
    const countryAreaChoice = validationRules.countryAreaChoices.find(
      ({ raw }) =>
        raw ===
        (COUNTRYWITHREVERSECODE?.includes(address.CountryName)
          ? address.SubAdministrativeArea
          : address.AdministrativeArea),
    );

    if (countryAreaChoice) {
      setter(`${withSchemaPrefix('countryArea')}`, {
        label: countryAreaChoice.verbose,
        value: countryAreaChoice.raw,
      });
    } else {
      setter(`${withSchemaPrefix('countryArea')}`, address.AdministrativeArea);
    }

    const cityChoice = validationRules.cityChoices.find(
      ({ verbose }) => verbose === address.Locality,
    );

    if (cityChoice) {
      setter(`${withSchemaPrefix('city')}`, {
        label: cityChoice.verbose,
        value: cityChoice.raw,
      });
    } else if (validationRules.cityChoices.length) {
      setter(`${withSchemaPrefix('city')}`, '');
    } else {
      setter(`${withSchemaPrefix('city')}`, address.Locality);
    }
  }

  clearErrors(
    [
      'postalCode',
      'city',
      'streetName',
      'streetNumber',
      'streetAddress2',
      'countryArea',
    ].map((field) => `${withSchemaPrefix(field)}`),
  );
};

export const fetchAddressById = async (
  id: string,
): Promise<{
  address: AddressFragment | null;
}> => {
  const client = initializeApollo();

  const { data } = await client.query<AddressQuery, AddressQueryVariables>({
    query: AddressDocument,
    variables: { id },
  });

  return {
    address: data?.address as AddressFragment | null,
  };
};

export const addAddress = async (
  opts: AccountAddressCreateMutationVariables,
): Promise<
  NonNullable<AccountAddressCreateMutation['accountAddressCreate']>
> => {
  const client = initializeApollo();

  const { data } = await client.mutate<
    AccountAddressCreateMutation,
    AccountAddressCreateMutationVariables
  >({
    mutation: AccountAddressCreateDocument,
    variables: {
      input: formatAddressForCountry({
        address: opts.input,
        isStreetAddress2SameAsCity: true,
      }),
    },
  });

  return {
    address: data?.accountAddressCreate?.address,
    errors: data?.accountAddressCreate?.errors || [],
    user: data?.accountAddressCreate?.user || null,
  };
};

export const updateAddress = async (
  opts: AccountAddressUpdateMutationVariables,
): Promise<
  NonNullable<AccountAddressUpdateMutation['accountAddressUpdate']>
> => {
  const client = initializeApollo();

  const { data } = await client.mutate<
    AccountAddressUpdateMutation,
    AccountAddressUpdateMutationVariables
  >({
    mutation: AccountAddressUpdateDocument,
    variables: {
      ...opts,
      input: formatAddressForCountry({
        address: opts.input,
        isStreetAddress2SameAsCity: true,
      }),
    },
  });

  return {
    address: data?.accountAddressUpdate?.address,
    errors: data?.accountAddressUpdate?.errors || [],
    user: data?.accountAddressUpdate?.user || null,
  };
};

export const deleteAddress = async (
  opts: AddressDeleteMutationVariables,
): Promise<NonNullable<AddressDeleteMutation['accountAddressDelete']>> => {
  const client = initializeApollo();

  const { data } = await client.mutate<
    AddressDeleteMutation,
    AddressDeleteMutationVariables
  >({
    mutation: AddressDeleteDocument,
    variables: opts,
  });

  return {
    address: data?.accountAddressDelete?.address,
    errors: data?.accountAddressDelete?.errors || [],
    user: data?.accountAddressDelete?.user || null,
  };
};

export const addressSetDefault = async (
  opts: AccountSetDefaultAddressMutationVariables,
): Promise<
  NonNullable<AccountSetDefaultAddressMutation['accountSetDefaultAddress']>
> => {
  const client = initializeApollo();

  const { data } = await client.mutate<
    AccountSetDefaultAddressMutation,
    AccountSetDefaultAddressMutationVariables
  >({
    mutation: AccountSetDefaultAddressDocument,
    variables: opts,
  });

  return {
    errors: data?.accountSetDefaultAddress?.errors || [],
    user: data?.accountSetDefaultAddress?.user || null,
  };
};

export const initAddressMetadata = (
  initMetadata: MetadataInput[],
  data: AddressSchema,
) => {
  const { abn, country, firstName, lastName, title, vatNumber } = data;
  const isJapan = country?.value === 'JP';
  const isUK = country?.value === 'GB';
  const isAustralia = country?.value === 'AU';

  // prevent sending duplicate metadata
  const dedupedMetadata = initMetadata.filter(
    (item) =>
      ![ACCOUNT_METADATA.vatNumber, ACCOUNT_METADATA.personalTitle].includes(
        item.key,
      ),
  );

  const kanjiMetadata = isJapan
    ? [
        ...(getMetaValue(ACCOUNT_METADATA.first_name_kanji, initMetadata)
          ? []
          : [{ key: ACCOUNT_METADATA.first_name_kanji, value: firstName }]),
        ...(getMetaValue(ACCOUNT_METADATA.last_name_kanji, initMetadata)
          ? []
          : [{ key: ACCOUNT_METADATA.last_name_kanji, value: lastName }]),
      ]
    : [];

  const titleMetadata = !isNil(title?.value)
    ? [{ key: ACCOUNT_METADATA.personalTitle, value: title?.value ?? '' }]
    : [];

  const taxMetadata = [
    ...(isUK
      ? [{ key: ACCOUNT_METADATA.vatNumber, value: vatNumber ?? '' }]
      : []),
    ...(isAustralia
      ? [{ key: ACCOUNT_METADATA.vatNumber, value: abn ?? '' }]
      : []),
  ];

  return [
    ...dedupedMetadata,
    ...titleMetadata,
    ...kanjiMetadata,
    ...taxMetadata,
  ];
};

export const isAddressWithPhone = (address?: AddressFragment | null) => {
  if (!address) return false;
  const phone =
    address.metadata?.find((item) => item.key === ACCOUNT_METADATA.phone) ||
    address.phone;
  return !!phone;
};

/**
 * A fully shaped but entirely blank address, for callers that must hand an
 * AddressFragment to a consumer that cannot accept null.
 *
 * Blank values are what make it harmless: every helper that compares it against
 * a real address reports "different", and every component that tests it for
 * completeness reports "incomplete".
 *
 * Exported as a shared frozen instance rather than a factory on purpose. React
 * effects keyed on an address prop compare by identity, so handing out a fresh
 * object per call would make them re-run on every render.
 */
export const EMPTY_ADDRESS_FRAGMENT: AddressFragment = Object.freeze({
  __typename: 'Address',
  id: '',
  city: '',
  cityArea: '',
  companyName: '',
  country: Object.freeze({
    __typename: 'CountryDisplay',
    code: '',
    country: '',
  }),
  countryArea: '',
  firstName: '',
  isDefaultBillingAddress: null,
  isDefaultShippingAddress: null,
  lastName: '',
  metadata: [],
  phone: null,
  postalCode: '',
  streetAddress1: '',
  streetAddress2: '',
});
